import { memo, useCallback, useRef } from "react";

import { isSixDigits } from "@/lib/authHelpers";
import { Input } from "@/shared/ui/input/Input";

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Called with the full 6-digit code as soon as it becomes complete. */
  onComplete?: (code: string) => void;
}

const BOX_COUNT = 6;

/**
 * Six-box one-time-code entry. Ports the `OtpSixInput` widget from
 * `src/app/verify-otp/page.tsx` onto the shared `Input` primitive:
 * per-digit boxes with paste-to-fill, arrow-key/backspace navigation, and
 * auto-advance. Emits `onComplete` once all six digits are present.
 */
function OtpInputBase({ value, onChange, disabled, onComplete }: OtpInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const safeVal = (value || "").replace(/\D/g, "").slice(0, BOX_COUNT);

  const emit = useCallback(
    (next: string) => {
      onChange(next);
      if (isSixDigits(next)) onComplete?.(next);
    },
    [onChange, onComplete]
  );

  const setChar = (idx: number, ch: string) => {
    const arr = safeVal.split("");
    arr[idx] = ch;
    onChange(arr.join("").padEnd(BOX_COUNT, ""));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setChar(idx, "");
      return;
    }
    if (raw.length > 1) {
      const merged = (safeVal.slice(0, idx) + raw + safeVal.slice(idx)).slice(0, BOX_COUNT);
      emit(merged);
      inputsRef.current[Math.min(BOX_COUNT - 1, idx + raw.length)]?.focus();
    } else {
      const candidate = (safeVal.slice(0, idx) + raw + safeVal.slice(idx + 1)).slice(0, BOX_COUNT);
      emit(candidate);
      if (idx < BOX_COUNT - 1) inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === "Backspace" && !safeVal[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === "ArrowRight" && idx < BOX_COUNT - 1) {
      inputsRef.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, idx: number) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, BOX_COUNT);
    if (!text) return;
    e.preventDefault();
    const merged = (safeVal.slice(0, idx) + text + safeVal.slice(idx)).slice(0, BOX_COUNT);
    emit(merged);
    inputsRef.current[Math.min(BOX_COUNT - 1, idx + text.length)]?.focus();
  };

  return (
    <div className="flex justify-center gap-2">
      {Array.from({ length: BOX_COUNT }).map((_, i) => (
        <Input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          aria-label={`Digit ${i + 1}`}
          data-otp-box="true"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={1}
          disabled={disabled}
          value={safeVal[i] ?? ""}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          className="h-12 w-10 text-center text-lg tracking-widest"
        />
      ))}
    </div>
  );
}

export const OtpInput = memo(OtpInputBase);
